import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FiTrendingUp, FiUsers, FiBook } from 'react-icons/fi';
import './AnalyticsCharts.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsCharts = ({ dashboardData }) => {
  if (!dashboardData) return null;

  const { students = {} } = dashboardData;
  const { total = 0, byEnrollmentStatus = {}, byAcademicStatus = {} } = students;

  // Calculate enrollment metrics
  const totalEnrolled = byEnrollmentStatus['Enrolled'] || 0;
  const totalNotEnrolled = byEnrollmentStatus['Not Enrolled'] || 0;
  const totalDropped = byEnrollmentStatus['Dropped'] || 0;
  const totalLOA = byEnrollmentStatus['Leave of Absence'] || byEnrollmentStatus['LOA'] || 0;

  // Filter out empty/zero values for cleaner charts
  const enrollmentLabels = [];
  const enrollmentValues = [];
  const enrollmentColors = [];
  const enrollmentBorderColors = [];

  if (totalEnrolled > 0) {
    enrollmentLabels.push('Enrolled');
    enrollmentValues.push(totalEnrolled);
    enrollmentColors.push('rgba(16, 185, 129, 0.8)');
    enrollmentBorderColors.push('rgba(16, 185, 129, 1)');
  }
  if (totalNotEnrolled > 0) {
    enrollmentLabels.push('Not Enrolled');
    enrollmentValues.push(totalNotEnrolled);
    enrollmentColors.push('rgba(245, 158, 11, 0.8)');
    enrollmentBorderColors.push('rgba(245, 158, 11, 1)');
  }
  if (totalDropped > 0) {
    enrollmentLabels.push('Dropped');
    enrollmentValues.push(totalDropped);
    enrollmentColors.push('rgba(239, 68, 68, 0.8)');
    enrollmentBorderColors.push('rgba(239, 68, 68, 1)');
  }
  if (totalLOA > 0) {
    enrollmentLabels.push('Leave of Absence');
    enrollmentValues.push(totalLOA);
    enrollmentColors.push('rgba(139, 92, 246, 0.8)');
    enrollmentBorderColors.push('rgba(139, 92, 246, 1)');
  }

  // Students by Enrollment Status Bar Chart
  const enrollmentBarData = {
    labels: enrollmentLabels,
    datasets: [{
      label: 'Students',
      data: enrollmentValues,
      backgroundColor: enrollmentColors,
      borderColor: enrollmentBorderColors,
      borderWidth: 2,
      borderRadius: 8,
    }]
  };

  // Enrollment Status Doughnut Chart
  const enrollmentDoughnutData = {
    labels: enrollmentLabels,
    datasets: [{
      data: enrollmentValues,
      backgroundColor: enrollmentColors,
      borderColor: 'white',
      borderWidth: 3,
    }]
  };

  // Academic Status Data - filter out empty values
  const academicLabels = [];
  const academicValues = [];
  const academicColors = [
    'rgba(16, 185, 129, 0.8)',  // Good Standing - green
    'rgba(59, 130, 246, 0.8)',  // Dean's List - blue
    'rgba(139, 92, 246, 0.8)',  // President's List - purple
    'rgba(245, 158, 11, 0.8)',  // Warning - yellow
    'rgba(239, 68, 68, 0.8)',   // Probation - red
    'rgba(107, 114, 128, 0.8)', // Other - gray
  ];

  Object.entries(byAcademicStatus).forEach(([status, count]) => {
    if (count > 0) {
      academicLabels.push(status);
      academicValues.push(count);
    }
  });

  const academicData = {
    labels: academicLabels,
    datasets: [{
      label: 'Students',
      data: academicValues,
      backgroundColor: academicColors.slice(0, academicLabels.length),
      borderColor: academicColors.slice(0, academicLabels.length).map(c => c.replace('0.8', '1')),
      borderWidth: 2,
      borderRadius: 8,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          family: "'Poppins', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        },
        cornerRadius: 8,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          },
          stepSize: 1
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        }
      }
    }
  };

  const horizontalBarOptions = {
    ...chartOptions,
    indexAxis: 'y',
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false
      }
    }
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2><FiTrendingUp /> 4th Year Analytics Dashboard</h2>
        <p>Comprehensive insights into 4th year student data</p>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-icon blue">
            <FiUsers />
          </div>
          <div className="summary-info">
            <span className="summary-value">{total}</span>
            <span className="summary-label">Total 4th Year Students</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon green">
            <FiBook />
          </div>
          <div className="summary-info">
            <span className="summary-value">{totalEnrolled}</span>
            <span className="summary-label">Currently Enrolled</span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {enrollmentLabels.length > 0 ? (
          <>
            <div className="chart-card">
              <h3>Students by Enrollment Status</h3>
              <div className="chart-wrapper">
                <Bar data={enrollmentBarData} options={chartOptions} />
              </div>
            </div>

            <div className="chart-card">
              <h3>Enrollment Distribution</h3>
              <div className="chart-wrapper doughnut">
                <Doughnut data={enrollmentDoughnutData} options={doughnutOptions} />
              </div>
            </div>
          </>
        ) : (
          <div className="chart-card wide">
            <div className="empty-chart">
              <FiUsers className="empty-icon" />
              <h3>No Enrollment Data</h3>
              <p>Student enrollment data will appear here once students are added.</p>
            </div>
          </div>
        )}

        {academicLabels.length > 0 ? (
          <div className="chart-card wide">
            <h3>Academic Standing Distribution</h3>
            <div className="chart-wrapper">
              <Bar data={academicData} options={horizontalBarOptions} />
            </div>
          </div>
        ) : (
          <div className="chart-card wide">
            <div className="empty-chart">
              <FiUsers className="empty-icon" />
              <h3>No Academic Status Data</h3>
              <p>Academic standing distribution will appear here once students have academic status assigned.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsCharts;
